import React from "react"

import { LocalizedText } from "../text/LocalizedText"
import { FlexLayout } from "../layout/flex/FlexLayout"
import { HotKeySet } from "../keyboard/HotKeyResponder"
import { ImageCapture } from "./ImageCapture"
import {
  DateOcrExtractionProvider,
  DateOcrExtractionResult,
} from "./DateOcrExtractionProvider"
import { withDateOcrProvider, WithDateOcrProvider } from "./withDateOcrProvider"
import { DateExtractionType } from "./DateExtractionType"
import { Button } from "../button/Button"
import { DangerousProps } from "../helpers/DangerousProps"
import { TestProps } from "../helpers/TestProps"
import { renderTestID } from "../helpers/renderTestID"

export interface DateExtractionStrings {
  /** Label for the capture button. */
  captureButton?: string
  /**
   * Heading for the internal error phase when the result carries a failure
   * reason. The string can use the `{reason}` MessageFormat placeholder.
   */
  extractionFailedWithReason?: string
  /** Fallback heading when no failure reason is available. */
  extractionFailed?: string
  /** Label for the retry button in the internal error phase. */
  retry?: string
}

export interface DateExtractionProps extends DangerousProps, TestProps {
  /** Form field name (result submitted as ${name}$json). */
  name: string
  /**
   * OCR provider used to extract a date from the captured image.
   * Takes precedence over the provider supplied via DateOcrProviderContext.
   */
  ocrProvider?: DateOcrExtractionProvider
  /** Type of date to extract (passed to the OCR provider). */
  extractionType?: DateExtractionType
  /**
   * Links the captured image to an audit record for backend persistence.
   * Passed through to the OCR provider. Required by AFTConanService, today's
   * only OCR backend; another may not need it.
   */
  auditId: string
  /** Scannable identifier of the container being processed. */
  containerScannableId: string
  /** FcSku of the item. At least one of fcSku or fnSku must be provided. */
  fcSku?: string
  /** FnSku of the item. At least one of fcSku or fnSku must be provided. */
  fnSku?: string
  /** Hot key for the capture button. */
  hotKey?: HotKeySet
  /**
   * Output image format for the captured image (default: "image/jpeg").
   *
   * JPEG is lossy but small, keeping request payloads well within backend
   * size limits. PNG is lossless and preserves sharp text edges for OCR
   * accuracy, at the cost of a much larger payload; when using PNG, rely on
   * `maxWidth`/`maxHeight` to keep the size in check.
   */
  imageType?: "image/jpeg" | "image/png"
  /**
   * JPEG compression quality from 0.0 to 1.0 (default: 1.0). Only applies when
   * `imageType` is "image/jpeg"; ignored for PNG, which is lossless. Higher
   * values preserve more detail for OCR at the cost of a larger payload.
   */
  imageQuality?: number
  /** Maximum width for captured image (default: 1280). */
  maxWidth?: number
  /** Maximum height for captured image (default: 720). */
  maxHeight?: number
  /** Automatically open camera on mount (default: false). */
  autoOpenCamera?: boolean
  /** Retries before reporting final failure (default: 0). */
  maxRetries?: number
  /** Disable automatic form submission after extraction (default: false). */
  disableAutoSubmit?: boolean
  /**
   * Callback invoked with the extraction result.
   * Used by parent components (e.g. DateCapture) to handle the result
   * without relying on form submission.
   */
  onResult?: (result: DateOcrExtractionResult) => void
  /**
   * Callback invoked when an image is captured, before OCR runs.
   * Receives the captured image's base64 payload and its content type so
   * parent components can preview the image alongside the OCR result.
   */
  onImageCaptured?: (
    base64Image: string,
    imageType: "image/jpeg" | "image/png"
  ) => void
}

type Phase = "idle" | "processing" | "error"

interface DateExtractionState {
  phase: Phase
  retryCount: number
  lastError: DateOcrExtractionResult | null
  outputJson: string
}

/**
 * DateExtraction captures an image via ImageCapture, runs it through
 * an OCR provider, and outputs the extraction result as JSON to a
 * hidden form input (using the ${name}$json convention).
 *
 * It uses the onCapture callback from ImageCapture to receive the
 * processed image data, then sends it to the OCR provider.
 *
 * On successful extraction, it writes the result to a hidden input
 * and auto-submits the parent form (unless disableAutoSubmit=true).
 *
 * On failure (after max retries exhausted), it also writes the failed
 * result to the hidden input and submits. The output JSON always
 * contains the full DateOcrExtractionResult with `success: false`
 * and `failureReason`, giving the backend enough context to handle
 * the error case.
 *
 * State machine:
 * - idle: waiting for user to capture an image
 * - processing: OCR extraction in progress
 * - error: OCR failed, user can retry
 */
export class DateExtractionBase extends React.PureComponent<
  DateExtractionProps & WithDateOcrProvider,
  DateExtractionState
> {

  public static defaultProps = {
    imageType: "image/jpeg" as const,
    imageQuality: 1.0,
    maxWidth: 1280,
    maxHeight: 720,
    autoOpenCamera: false,
    maxRetries: 0,
    disableAutoSubmit: false,
  }

  /** Override to customize strings or provide translations. */
  public static defaultStrings?: DateExtractionStrings

  private readonly hiddenInputRef = React.createRef<HTMLInputElement>()

  // Stable empty fallback so render references don't churn.
  private readonly emptyStrings: DateExtractionStrings = {}

  public constructor(props: DateExtractionProps & WithDateOcrProvider) {
    super(props)
    this.state = {
      phase: "idle",
      retryCount: 0,
      lastError: null,
      outputJson: "",
    }
  }

  private handleImageCaptured = (base64Image: string) => {
    if (this.props.onImageCaptured) {
      this.props.onImageCaptured(base64Image, this.props.imageType!)
    }
    this.runExtraction(base64Image)
  }

  private async runExtraction(base64Image: string) {
    const {
      extractionType,
      auditId,
      containerScannableId,
      fcSku,
      fnSku,
      maxRetries = 0,
    } = this.props

    const provider = this.props.ocrProvider ?? this.props.contextOcrProvider
    this.setState({ phase: "processing" })

    let result: DateOcrExtractionResult
    try {
      result = await provider.extractDate(base64Image, this.props.imageType!, {
        extractionType,
        auditId,
        containerScannableId,
        fcSku,
        fnSku,
      })
    } catch (err: any) {
      const retryCount = this.state.retryCount + 1
      const errorResult: DateOcrExtractionResult = {
        success: false,
        failureReason: "ERROR",
        errorMessage: err?.message || "Unknown error",
      }
      if (retryCount > maxRetries) {
        this.completeWithResult(errorResult)
      } else {
        this.setState({
          phase: "error",
          retryCount,
          lastError: errorResult,
        })
      }
      return
    }

    if (result.success) {
      this.completeWithResult(result)
    } else {
      const retryCount = this.state.retryCount + 1
      if (retryCount > maxRetries) {
        this.completeWithResult(result)
      } else {
        this.setState({
          phase: "error",
          retryCount,
          lastError: result,
        })
      }
    }
  }

  private submitForm = () => {
    const formElement = this.hiddenInputRef.current?.form
    if (formElement) {
      formElement.requestSubmit()
    }
  }

  private completeWithResult(result: DateOcrExtractionResult) {
    this.setState({
      phase: "idle",
      retryCount: 0,
      outputJson: JSON.stringify(result),
    }, () => {
      if (this.props.onResult) {
        this.props.onResult(result)
      }
      if (!this.props.disableAutoSubmit) {
        this.submitForm()
      }
    })
  }

  private handleRetry = () => {
    this.setState({ phase: "idle" })
  }

  private renderIdle() {
    const {
      hotKey, imageType, imageQuality, maxWidth, maxHeight, autoOpenCamera,
    } = this.props
    const strings =
      DateExtractionBase.defaultStrings || this.emptyStrings
    return (
      <ImageCapture
        name={`${this.props.name}__image`}
        disableAutoSubmit={true}
        imageType={imageType}
        imageQuality={imageQuality}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        hotKey={hotKey}
        autoOpenCamera={autoOpenCamera}
        onCapture={this.handleImageCaptured}
      >
        <LocalizedText
          stringID={strings.captureButton}
          defaultString="Capture Date"
        />
      </ImageCapture>
    )
  }

  private renderProcessing() {
    // No visible processing UI here. Parents (e.g. DateCapture) render
    // status feedback during in-flight OCR.
    return null
  }

  private renderError() {
    const { lastError } = this.state
    const strings =
      DateExtractionBase.defaultStrings || this.emptyStrings
    return (
      <FlexLayout direction="column">
        <FlexLayout.Item size="auto" padding="sm">
          {/* TODO: map failureReason to a friendly per-reason message. */}
          {lastError?.failureReason ? (
            <LocalizedText
              stringID={strings.extractionFailedWithReason}
              defaultString="Extraction failed: {reason}"
              parameters={{ reason: lastError.failureReason }}
            />
          ) : (
            <LocalizedText
              stringID={strings.extractionFailed}
              defaultString="Extraction failed"
            />
          )}
        </FlexLayout.Item>
        <FlexLayout.Item size="auto" padding="sm">
          <Button
            variant="primary"
            fluid={true}
            onClick={this.handleRetry}
          >
            <LocalizedText
              stringID={
                (DateExtractionBase.defaultStrings || this.emptyStrings).retry
              }
              defaultString="Retry"
            />
          </Button>
        </FlexLayout.Item>
      </FlexLayout>
    )
  }

  public render() {
    const { name, dangerousClassName, dangerousStyle, testID } = this.props
    const { phase, outputJson } = this.state

    return (
      <div
        className={dangerousClassName}
        style={dangerousStyle}
        data-testid={renderTestID(testID)}
      >
        {phase === "idle" && this.renderIdle()}
        {phase === "processing" && this.renderProcessing()}
        {phase === "error" && this.renderError()}

        <input
          ref={this.hiddenInputRef}
          type="hidden"
          name={`${name}$json`}
          value={outputJson}
        />
      </div>
    )
  }
}

/**
 * DateExtraction with the OCR provider injected from DateOcrProviderContext.
 * A per-instance `ocrProvider` prop still takes precedence over the context.
 */
export const DateExtraction = withDateOcrProvider(DateExtractionBase)
